import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Users,
  Mail,
  Phone,
  Award,
  Building,
  User,
  Calendar,
  AlertCircle,
  Clock,
  Plane,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  TrendingUp,
  Filter,
  X as XIcon
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useDialog } from '../contexts/DialogContext';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const Crew = () => {
  const { confirm } = useDialog();
  const [crew, setCrew] = useState([]);
  const [filteredCrew, setFilteredCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'captain' | 'first_officer' | 'cabin_crew' | 'pilot' | 'is_me'

  // Track which cards are expanded and their flight data
  const [expandedCards, setExpandedCards] = useState({});       // { [id]: bool }
  const [flightStats, setFlightStats]     = useState({});       // { [id]: { loading, data, error } }

  const fetchCrew = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/crew', getAuthHeaders());
      setCrew(response.data);
    } catch (error) {
      console.error('Error fetching crew:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to view crew members');
      } else {
        toast.error('Failed to load crew members');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCrew();
  }, [fetchCrew]);

  // Fetch flight stats for a single crew member (only called once per member)
  const fetchFlightStats = useCallback(async (memberId) => {
    setFlightStats(prev => ({
      ...prev,
      [memberId]: { loading: true, data: null, error: null }
    }));
    try {
      const response = await axios.get(`/api/crew/${memberId}/flights`, getAuthHeaders());
      setFlightStats(prev => ({
        ...prev,
        [memberId]: { loading: false, data: response.data, error: null }
      }));
    } catch (error) {
      console.error(`Error fetching flights for crew ${memberId}:`, error);
      setFlightStats(prev => ({
        ...prev,
        [memberId]: { loading: false, data: null, error: 'Failed to load flight history' }
      }));
    }
  }, []);

  const toggleExpand = (memberId) => {
    const isCurrentlyExpanded = expandedCards[memberId];
    setExpandedCards(prev => ({ ...prev, [memberId]: !isCurrentlyExpanded }));

    // Fetch stats the first time this card is expanded
    if (!isCurrentlyExpanded && !flightStats[memberId]) {
      fetchFlightStats(memberId);
    }
  };

  const deleteCrew = async (id, name) => {
    if (await confirm({ title: `Deactivate ${name}?`, message: 'Their flight history is kept, but they\'ll be removed from active crew lists.', confirmLabel: 'Deactivate', tone: 'danger' })) {
      try {
        await axios.delete(`/api/crew/${id}`, getAuthHeaders());
        toast.success('Crew member deactivated successfully');
        fetchCrew();
      } catch (error) {
        console.error('Error deactivating crew member:', error);
        if (error.response?.status === 401) {
          toast.error('Please log in to delete crew members');
        } else {
          toast.error('Failed to deactivate crew member');
        }
      }
    }
  };
  
  useEffect(() => {
    let filtered = [...crew];

    // Role / category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(member => {
        const pos = (member.position || '').toLowerCase();
        const certType = (member.certificate_type || '').toLowerCase();
        const crewFunc = (member.crew_function || '').toLowerCase();

        switch (activeFilter) {
          case 'captain':
            return pos.includes('captain') || pos === 'ca';
          case 'first_officer':
            return pos.includes('first officer') || pos === 'fo';
          case 'cabin_crew':
            return pos.includes('cabin') || pos.includes('flight attendant') ||
                   crewFunc.includes('cabin') || ['fa','fb','fc','fd'].includes(pos);
          case 'pilot':
            return certType.includes('airline_transport') || certType.includes('commercial') ||
                   certType.includes('private') || pos.includes('captain') ||
                   pos.includes('first officer') || pos.includes('pilot') ||
                   member.pic === 1 || member.sic === 1;
          case 'is_me':
            return member.is_me === 1 || member.is_me === true;
          default:
            return true;
        }
      });
    }

    // Search on top of role filter
    if (searchTerm) {
      filtered = filtered.filter(member =>
        member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCrew(filtered);
  }, [crew, searchTerm, activeFilter]);

  // Badge counts derived from the full unfiltered list
  const filterCounts = {
    all:           crew.length,
    captain:       crew.filter(m => (m.position||'').toLowerCase().includes('captain') || m.position === 'CA').length,
    first_officer: crew.filter(m => (m.position||'').toLowerCase().includes('first officer') || m.position === 'FO').length,
    cabin_crew:    crew.filter(m => {
      const p = (m.position||'').toLowerCase();
      return p.includes('cabin') || p.includes('flight attendant') || ['fa','fb','fc','fd'].includes(p);
    }).length,
    pilot:         crew.filter(m => {
      const c = (m.certificate_type||'').toLowerCase();
      const p = (m.position||'').toLowerCase();
      return c.includes('airline_transport') || c.includes('commercial') || c.includes('private') ||
             p.includes('captain') || p.includes('first officer') || p.includes('pilot') ||
             m.pic === 1 || m.sic === 1;
    }).length,
    is_me:         crew.filter(m => m.is_me === 1 || m.is_me === true).length,
  };

  const getCertificateDisplay = (certType) => {
    const types = {
      'student': 'Student',
      'sport': 'Sport',
      'recreational': 'Recreational', 
      'private': 'Private',
      'commercial': 'Commercial',
      'airline_transport': 'ATP',
      'flight_instructor': 'CFI'
    };
    return types[certType] || 'Unknown';
  };

  const getMedicalClassDisplay = (medClass) => {
    const classes = {
      'first': 'First Class',
      'second': 'Second Class',
      'third': 'Third Class',
      'basic_med': 'BasicMed',
      'sport': 'Sport'
    };
    return classes[medClass] || 'Unknown';
  };

  const isNearExpiry = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diffDays <= 90 && diffDays >= 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '0.0';
    return parseFloat(hours).toFixed(1);
  };

  // ─── Flight Stats Panel ───────────────────────────────────────────────────
  const FlightStatsPanel = ({ memberId }) => {
    const stats = flightStats[memberId];

    if (!stats || stats.loading) {
      return (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-aviation-600"></div>
          <span className="ml-2 text-sm text-gray-500">Loading flight history…</span>
        </div>
      );
    }

    if (stats.error) {
      return (
        <div className="flex items-center space-x-2 py-4 text-red-500">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{stats.error}</p>
        </div>
      );
    }

    const { summary, recent_flights } = stats.data || {};

    if (!summary || summary.total_flights === 0) {
      return (
        <div className="py-4 text-center">
          <Plane className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No shared flights logged yet</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Summary stats row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-aviation-50 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center mb-1">
              <Plane className="h-3.5 w-3.5 text-aviation-600" />
            </div>
            <p className="text-lg font-bold text-aviation-700">{summary.total_flights}</p>
            <p className="text-xs text-gray-500">Flights</p>
          </div>
          <div className="bg-aviation-50 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center mb-1">
              <Clock className="h-3.5 w-3.5 text-aviation-600" />
            </div>
            <p className="text-lg font-bold text-aviation-700">{formatHours(summary.total_hours)}</p>
            <p className="text-xs text-gray-500">Hours</p>
          </div>
          <div className="bg-aviation-50 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center mb-1">
              <TrendingUp className="h-3.5 w-3.5 text-aviation-600" />
            </div>
            <p className="text-lg font-bold text-aviation-700">{formatHours(summary.avg_hours_per_flight)}</p>
            <p className="text-xs text-gray-500">Avg hrs</p>
          </div>
        </div>

        {/* Last flown */}
        {summary.last_flight_date && (
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <Calendar className="h-3 w-3" />
            <span>Last flown together: <span className="font-medium text-gray-700">{formatDate(summary.last_flight_date)}</span></span>
          </div>
        )}

        {/* Recent flights list */}
        {recent_flights && recent_flights.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Recent Flights</p>
            <div className="space-y-1.5">
              {recent_flights.map((flight) => (
                <div
                  key={flight.id}
                  className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-xs"
                >
                  <div className="flex items-center space-x-2 min-w-0">
                    <span className="text-gray-400 flex-shrink-0">{formatDate(flight.date)}</span>
                    <span className="font-medium text-gray-700 truncate">
                      {flight.departure_airport} → {flight.arrival_airport}
                    </span>
                    {flight.aircraft_type && (
                      <span className="text-gray-400 flex-shrink-0 hidden sm:inline">{flight.aircraft_type}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    {flight.crew_role && (
                      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                        flight.crew_role === 'PIC'
                          ? 'bg-aviation-100 text-aviation-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {flight.crew_role}
                      </span>
                    )}
                    <span className="font-medium text-aviation-700">{formatHours(flight.duration_hours)}h</span>
                    {flight.id && (
                      <Link
                        to={`/flights/${flight.id}`}
                        className="text-aviation-400 hover:text-aviation-600"
                        title="View flight"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Link to full history */}
        <div className="text-center pt-1">
          <Link
            to={`/flights?crew=${memberId}`}
            className="text-xs text-aviation-600 hover:text-aviation-700 font-medium inline-flex items-center space-x-1"
            onClick={(e) => e.stopPropagation()}
          >
            <span>View all shared flights</span>
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviation-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
              <Users className="h-8 w-8 mr-3 text-aviation-600" />
              Crew Management
            </h1>
            <p className="text-gray-600">
              Manage pilots, instructors, and crew members you've flown with
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link 
              to="/add-crew" 
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Crew Member</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Role Filters */}
      <div className="card mb-4">
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-600">Filter by role</span>
          {activeFilter !== 'all' && (
            <button
              onClick={() => setActiveFilter('all')}
              className="ml-auto flex items-center space-x-1 text-xs text-aviation-600 hover:text-aviation-700 font-medium"
            >
              <XIcon className="h-3 w-3" />
              <span>Clear filter</span>
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all',           label: 'All Crew' },
            { key: 'captain',       label: 'Captains' },
            { key: 'first_officer', label: 'First Officers' },
            { key: 'pilot',         label: 'All Pilots' },
            { key: 'cabin_crew',    label: 'Cabin Crew' },
            { key: 'is_me',         label: 'Me' },
          ].map(({ key, label }) => {
            const count = filterCounts[key];
            const isActive = activeFilter === key;
            if (count === 0 && key !== 'all') return null; // hide empty categories
            return (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-aviation-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-aviation-500 text-white' : 'bg-white text-gray-500'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="card mb-6">
        <div className="relative">
          <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search crew members by name, company, position, or certificate number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Crew Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCrew.map((member) => (
          <div key={member.id} className="card hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="aviation-gradient p-2 rounded-lg">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-gray-900 truncate">
                    {member.name}
                  </h3>
                  {member.certificate_number && (
                    <p className="text-sm text-gray-600 truncate">
                      {getCertificateDisplay(member.certificate_type)} • {member.certificate_number}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <Link
                  to={`/edit-crew/${member.id}`}
                  className="text-aviation-600 hover:text-aviation-700 p-1"
                  title="Edit Crew Member"
                >
                  <Edit className="h-4 w-4" />
                </Link>
                <button
                  onClick={() => deleteCrew(member.id, member.name)}
                  className="text-red-600 hover:text-red-700 p-1"
                  title="Deactivate Crew Member"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {/* Company & Position */}
              {(member.company || member.position) && (
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-600 truncate">
                    {member.company}{member.company && member.position && ' • '}{member.position}
                  </p>
                </div>
              )}

              {/* Email */}
              {member.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <a 
                    href={`mailto:${member.email}`}
                    className="text-sm text-aviation-600 hover:text-aviation-700 truncate"
                  >
                    {member.email}
                  </a>
                </div>
              )}

              {/* Phone */}
              {member.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <a 
                    href={`tel:${member.phone}`}
                    className="text-sm text-aviation-600 hover:text-aviation-700"
                  >
                    {member.phone}
                  </a>
                </div>
              )}

              {/* Medical Information */}
              {member.medical_class && (
                <div className="flex items-center space-x-2">
                  <Award className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600">
                      {getMedicalClassDisplay(member.medical_class)} Medical
                    </p>
                    {member.medical_expiry && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        <p className={`text-xs ${
                          isExpired(member.medical_expiry) ? 'text-red-600' :
                          isNearExpiry(member.medical_expiry) ? 'text-yellow-600' :
                          'text-gray-500'
                        }`}>
                          Expires: {formatDate(member.medical_expiry)}
                        </p>
                        {(isExpired(member.medical_expiry) || isNearExpiry(member.medical_expiry)) && (
                          <AlertCircle className={`h-3 w-3 ${
                            isExpired(member.medical_expiry) ? 'text-red-600' : 'text-yellow-600'
                          }`} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Experience Hours */}
              {member.experience_hours && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Experience:</span> {member.experience_hours} hours
                </div>
              )}

              {/* Ratings */}
              {member.ratings && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Ratings:</p>
                  <div className="flex flex-wrap gap-1">
                    {member.ratings.split(',').map((rating, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-aviation-100 text-aviation-700"
                      >
                        {rating.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {member.notes && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Notes:</p>
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {member.notes}
                  </p>
                </div>
              )}
            </div>

            {/* ── Flight History Toggle ─────────────────────────────────── */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={() => toggleExpand(member.id)}
                className="w-full flex items-center justify-between text-sm font-medium text-aviation-600 hover:text-aviation-700 transition-colors"
              >
                <span className="flex items-center space-x-1.5">
                  <Plane className="h-4 w-4" />
                  <span>Flight History Together</span>
                </span>
                {expandedCards[member.id]
                  ? <ChevronUp className="h-4 w-4" />
                  : <ChevronDown className="h-4 w-4" />
                }
              </button>

              {expandedCards[member.id] && (
                <div className="mt-3">
                  <FlightStatsPanel memberId={member.id} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredCrew.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">
            {searchTerm
              ? 'No crew members match your search'
              : activeFilter !== 'all'
                ? 'No crew members match the selected filter'
                : 'No crew members added yet'
            }
          </p>
          <Link
            to="/add-crew"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Your First Crew Member</span>
          </Link>
        </div>
      )}

      {/* Crew Count */}
      {filteredCrew.length > 0 && (
        <div className="mt-6 text-sm text-gray-600 text-center">
          Showing {filteredCrew.length} of {crew.length} crew members
          {activeFilter !== 'all' && (
            <span className="ml-1 text-aviation-600 font-medium">
              · filtered by {
                { captain: 'Captains', first_officer: 'First Officers',
                  pilot: 'All Pilots', cabin_crew: 'Cabin Crew', is_me: 'Me' }[activeFilter]
              }
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default Crew;